// Clearing a PC's health warnings by hand (F-STN-11, spec/13 §3.3; 2026-10-02): one warning or all of them, with an
// optional note, from the Health tab and from the floor tile's quick menu — `POST …/stations/{s}/alerts/clear`,
// `stations:alerts_clear`, audited. A warning the PC still reports comes back, so the form says so and offers Snooze
// (hide it from the badge and "Needs attention" for a few hours) as the way to quiet a known problem.

import type { FloorStationState } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, SegmentedControl, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Icon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { useClearStationAlerts } from './queries';
import { useProblem } from '../api/use-problem';

/** The permission that clears or snoozes a station's alerts. */
export const STATIONS_ALERTS_CLEAR = 'stations:alerts_clear';

const SNOOZE_HOURS = ['1', '4', '24'] as const;

/** The note's limit (the contract's `ClearStationAlertsRequest.note`). */
const NOTE_MAX = 500;

export function ClearAlertsForm({
  station,
  alertIds,
  onDone,
}: {
  readonly station: Pick<FloorStationState, 'stationId'>;
  /** The alerts to clear; absent clears every open alert on the station. */
  readonly alertIds?: readonly string[];
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const clear = useClearStationAlerts(venue.organizationId, venue.id);
  const [note, setNote] = useState('');
  const [hours, setHours] = useState<string>('4');
  const [error, setError] = useState<string | undefined>(undefined);
  const title = alertIds !== undefined && alertIds.length === 1 ? t('floor.drawer.health.clearForm.titleOne') : t('floor.drawer.health.clearForm.titleAll');

  const send = (snooze: boolean) => {
    const line = note.trim();
    setError(undefined);
    clear.mutate(
      {
        stationId: station.stationId,
        body: {
          ...(alertIds === undefined ? {} : { alertIds: [...alertIds] }),
          ...(line === '' ? {} : { note: line }),
          ...(snooze ? { snoozeHours: Number(hours) } : {}),
        },
      },
      {
        onSuccess: (result) => {
          const count = result.alerts.length;
          show({
            tone: 'ok',
            title:
              count === 0
                ? t('floor.drawer.health.clearForm.nothing')
                : snooze
                  ? t('floor.drawer.health.clearForm.snoozed', { count })
                  : t('floor.drawer.health.clearForm.cleared', { count }),
          });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('floor.drawer.health.clearForm.failed') })),
      },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send(false);
  };

  return (
    <form className="console-form console-health__clear" aria-label={title} noValidate onSubmit={submit}>
      <p className="console-muted">{t('floor.drawer.health.clearForm.hint')}</p>
      <TextField
        label={t('floor.drawer.health.clearForm.note')}
        placeholder={t('floor.drawer.health.clearForm.notePlaceholder')}
        maxLength={NOTE_MAX}
        value={note}
        onChange={(event) => setNote(event.target.value)}
        error={error}
      />
      <div className="console-station-sheet__row">
        <Button type="submit" variant="secondary" icon={<Icon name="check" />} busy={clear.isPending && clear.variables?.body.snoozeHours === undefined}>
          {t('floor.drawer.health.clearForm.clear')}
        </Button>
        <SegmentedControl
          size="sm"
          label={t('floor.drawer.health.clearForm.snoozeFor')}
          value={hours}
          onValueChange={setHours}
          options={SNOOZE_HOURS.map((value) => ({ value, label: t('floor.drawer.health.clearForm.hours', { n: Number(value) }) }))}
        />
        <Button type="button" variant="ghost" busy={clear.isPending && clear.variables?.body.snoozeHours !== undefined} onClick={() => send(true)}>
          {t('floor.drawer.health.clearForm.snooze')}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('floor.drawer.health.clearForm.cancel')}
        </Button>
      </div>
    </form>
  );
}
